/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { Typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import GradePercentDisplay from 'bundles/grading/components/common/GradePercentDisplay';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import ControlsDialog from 'bundles/grading/components/staff-graded-assignments/grade-override/ControlsDialog';
import ErrorNotification from 'bundles/grading/components/staff-graded-assignments/grade-override/ErrorNotification';
import { generateDeleteGradeOverrideMutationVariables } from 'bundles/grading/utils/apiUtils';

import _t from 'i18n!nls/grading';

const styles = {
  verticalAlignRow: () => css`
    display: flex;
    align-items: center;
  `,
  grade: () => css`
    margin-left: var(--cds-spacing-100);
  `,
  arrowIcon: () => css`
    margin: 0 var(--cds-spacing-200);
  `,
  deleteOverrideDialogPrompt: () => css`
    margin-bottom: var(--cds-spacing-100);
  `,
};
// Component for dialog to delete grade override
const DeleteGradeOverrideDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { mutations, pageData, submissionData } = useContext(SGAPageContext);
  const { gradebookEntry } = submissionData.data || {};
  // deletes grade override
  const deleteGradeOverride = async () => {
    await mutations?.deleteGradeOverride({
      variables: generateDeleteGradeOverrideMutationVariables(
        submissionData.data?.groupId || '',
        pageData.currentItemId,
        submissionData.data?.submitter?.id || '',
        pageData.gradingGroupId
      ),
    });
    onClose();
  };

  if (!mutations) {
    return null;
  }

  return (
    <ControlsDialog
      onClose={onClose}
      heading={_t('Delete grade override')}
      primaryButtonLabel={_t('Delete')}
      primaryButtonDisabled={mutations?.deleteGradeOverrideStatus.loading}
      primaryButtonOnClick={deleteGradeOverride}
      applyingOnClickAction={mutations?.deleteGradeOverrideStatus.loading}
      secondaryButtonDisabled={mutations?.deleteGradeOverrideStatus.loading}
      dataTestidPrefix="delete-grade-override-dialog"
    >
      <Typography2 component="p" css={styles.deleteOverrideDialogPrompt}>
        {_t('Are you sure you want to delete this grade override?')}
      </Typography2>
      <Typography2 component="p" variant="bodyPrimary" css={styles.verticalAlignRow}>
        {_t('Override grade:')}
        <GradePercentDisplay grade={gradebookEntry?.overrideOutcome?.grade} cssOverride={styles.grade} />
        <ArrowNextIcon size="large" css={styles.arrowIcon} />
        {_t('Original grade:')}
        <GradePercentDisplay grade={gradebookEntry?.originalOutcome?.grade} cssOverride={styles.grade} />
      </Typography2>
      <ErrorNotification isApplyOverrideDialog={false} />
    </ControlsDialog>
  );
};

export default DeleteGradeOverrideDialog;
